'use client';

import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useForm } from 'react-hook-form';

import { fetchJson } from 'lib/fetch-json';
import { EMAIL_REGEX } from 'lib/constants';

import useRecaptcha from 'src/hooks/useRecaptcha';
import Button from 'src/components/Button';
import FormError from 'src/components/FormError';

const AnimatedSmiley = dynamic(() => import('src/components/AnimatedSmiley'), {
    ssr: false,
});

export default function PageForm({ locationsByCity }) {
    const { isReady, generateToken } = useRecaptcha();
    const [errorMessage, setErrorMessage] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [locations, setLocations] = useState([]);

    const [states] = useState(
        Object.values(
            locationsByCity.reduce(
                (acc: any, l: any) => ({
                    ...acc,
                    [l.city.state.slug]: { ...l.city.state },
                }),
                {},
            ),
        ).sort((a: any, b: any) => a.name?.localeCompare(b.name)),
    );

    const {
        register,
        formState: { errors: _errors },
        handleSubmit,
        setValue,
        watch,
    } = useForm({
        defaultValues: {
            email: '',
            phone: '',
            message: '',
            state: '',
            location: '',
        },
    });

    const errors: any = _errors;

    const state = watch('state');
    useEffect(() => {
        if (locationsByCity) {
            setValue('location', '');
            setLocations(locationsByCity.filter((l) => state && l?.city?.state?.slug === state));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, setValue]);

    const onSubmit = async (params: any) => {
        setLoading(true);
        setErrorMessage(false);
        try {
            const recaptcha = await generateToken();

            await fetchJson(`/api/forms/contact`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...params, recaptcha }),
            });

            setLoading(false);
            setSubmitted(true);
        } catch (error: any) {
            setLoading(false);
            setErrorMessage(true);
        }
    };

    return (
        <>
            {errorMessage && (
                <div className="text-center">
                    <div className="aspect-1 mx-auto w-3/4 text-red-400 md:w-1/2">
                        <AnimatedSmiley variant="sad" />
                    </div>
                    <h2 className="mb-4 text-3xl md:text-4xl">Eroare</h2>
                    <p>
                        Ne pare rău! A apărut o eroare la trimiterea formularului de contact. Te rugăm să încerci din
                        nou în câteva minute.
                    </p>
                    <a
                        href="#"
                        className="btn btn-white mt-4 inline-block px-8"
                        onClick={(event) => {
                            setErrorMessage(false);
                            event.preventDefault();
                        }}>
                        Încearcă din nou
                    </a>
                </div>
            )}

            {!errorMessage && !submitted && (
                <>
                    <h2 className="mb-4 text-3xl md:text-4xl">Trimite un mesaj</h2>
                    <form id="contact-form" noValidate onSubmit={handleSubmit(onSubmit)}>
                        <div className="flex flex-col gap-4">
                            <div>
                                <input
                                    id="email"
                                    className={errors.email ? 'form-error' : ''}
                                    type="email"
                                    placeholder="Email*"
                                    {...register('email', {
                                        required: 'Completarea adresei de email este obligatorie',
                                        pattern: {
                                            value: EMAIL_REGEX,
                                            message: 'Adresa de email este incorectă',
                                        },
                                    })}
                                />
                                <FormError error={errors.email} />
                            </div>

                            <div>
                                <input
                                    id="phone"
                                    className={errors.phone ? 'form-error' : ''}
                                    type="tel"
                                    placeholder="Telefon"
                                    {...register('phone')}
                                />
                                <FormError error={errors.phone} />
                            </div>

                            <div>
                                <select
                                    id="state"
                                    className={errors.state ? 'form-error' : ''}
                                    {...register('state', {
                                        required: 'Completarea județului este obligatorie',
                                    })}>
                                    <option value="">Alegeți județul*</option>
                                    {states?.map((state: any) => (
                                        <option key={state.slug} value={state.slug}>
                                            {state.name}
                                        </option>
                                    ))}
                                </select>
                                <FormError error={errors.state} />
                            </div>

                            <div>
                                <select
                                    id="location"
                                    className={errors.location ? 'form-error' : ''}
                                    disabled={!locations?.length}
                                    {...register('location', {
                                        required: 'Completarea locației este obligatorie',
                                    })}>
                                    <option value="">Selectați o locație*</option>
                                    {locations?.map((cl: any) => (
                                        <optgroup key={cl.city.slug} label={cl.city.name}>
                                            {cl.locations?.map((l: any) => (
                                                <option key={l.code} value={l.code}>
                                                    {l.name}
                                                </option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                                <FormError error={errors.location} />
                            </div>

                            <div>
                                <textarea
                                    id="message"
                                    className={errors.message ? 'form-error' : ''}
                                    placeholder="Scrie-ne cu ce te putem ajuta"
                                    style={{ height: '150px', resize: 'none' }}
                                    {...register('message', {
                                        required: 'Completarea mesajului este obligatorie',
                                    })}
                                />
                                <FormError error={errors.message} />
                            </div>

                            <div>
                                <Button type="submit" variant="secondary" size="lg" disabled={loading || !isReady}>
                                    Trimite
                                </Button>
                            </div>
                        </div>
                    </form>
                </>
            )}

            {submitted && (
                <div className="text-center">
                    <div className="text-light-green aspect-1 mx-auto w-3/4 md:w-1/2">
                        <AnimatedSmiley variant="happy" />
                    </div>
                    <h2 className="mb-4 text-3xl md:text-4xl">Succes!</h2>
                    <p>
                        Mulțumim pentru feedback-ul dumneavoastră! Încercăm să vă răspundem în cel mai scurt timp
                        posibil.
                    </p>
                </div>
            )}
        </>
    );
}
